import { resolveApiBase, SportsApiClient, text, toArray, type WidgetProps } from '@sports/core'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'

export const GOLF_LEADERBOARD_ATTRIBUTES = [
  'tour',
  'lang',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface GolfLeaderboardProps extends WidgetProps {
  tour?: string
  lang?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

export const golfLeaderboardStyles = `
  .gview { display: flex; gap: .4rem; padding: .5rem 1rem; border-top: 1px solid var(--sports-border); }
  .gview button {
    font: inherit; font-size: .78rem; font-weight: 600; cursor: pointer;
    padding: .25rem .7rem; border-radius: 6px; border: 1px solid var(--sports-border);
    background: var(--sports-card); color: var(--sports-muted);
  }
  .gview button.on { background: var(--sports-accent); border-color: var(--sports-accent); color: #fff; }
  .tour { padding: 0.4rem 1rem; font-size: 0.72rem; color: var(--sports-muted);
    text-transform: uppercase; letter-spacing: 0.04em; border-top: 1px solid var(--sports-border); }
  .row {
    display: grid; grid-template-columns: 2rem 1fr auto auto; gap: 0.75rem; align-items: center;
    padding: 0.4rem 1rem; border-top: 1px solid var(--sports-border); font-size: 0.875rem;
  }
  .pos { color: var(--sports-muted); text-align: right; font-variant-numeric: tabular-nums; }
  .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .par { font-variant-numeric: tabular-nums; font-weight: 600; text-align: right; min-width: 2.4rem; }
  .par.under { color: var(--sports-accent); }
  .thru { color: var(--sports-muted); font-size: 0.78rem; text-align: right; min-width: 2.6rem; }
  /* Pairings (tee-time flights) — golf's closest thing to fixtures. */
  .flight { border-top: 1px solid var(--sports-border); }
  .flight-t {
    padding: .35rem 1rem; font-size: .74rem; font-weight: 700; color: var(--sports-accent);
    background: color-mix(in srgb, var(--sports-fg) 4%, transparent); font-variant-numeric: tabular-nums;
  }
  .flight-p { display: grid; grid-template-columns: 1fr auto; gap: .5rem; padding: .35rem 1rem .35rem 1.6rem; font-size: .86rem; }
  .flight-p .par { min-width: 2.4rem; }
`

// Live golf feeds per tour (registry extraFeeds). PGA uses the default `live`.
const TOUR_FEED: Record<string, string> = {
  pga: 'live',
  dp: 'european_live',
  european: 'european_live',
  liv: 'liv_live',
  lpga: 'lpga_live',
  champions: 'champions_live',
}

interface GolfRow {
  pos: string
  name: string
  toPar: string
  thru: string
  teeTime: string
}

function parseLeaderboard(payload: unknown): { name: string; rows: GolfRow[] } {
  const scores = (payload as { scores?: unknown })?.scores
  // The tournament node is the one holding `player`; find it structurally.
  let node: Record<string, unknown> | null = null
  const walk = (o: unknown, depth: number): void => {
    if (node || depth > 4 || !o || typeof o !== 'object') return
    if (Array.isArray(o)) {
      o.forEach((c) => walk(c, depth + 1))
      return
    }
    const rec = o as Record<string, unknown>
    if (rec.player) {
      node = rec
      return
    }
    Object.values(rec).forEach((v) => walk(v, depth + 1))
  }
  walk(scores, 0)
  if (!node) return { name: '', rows: [] }

  const players = toArray<Record<string, unknown>>(
    (node as Record<string, unknown>).player as Record<string, unknown>[] | undefined,
  )
  const rows = players.map((p) => ({
    pos: text(p, 'pos'),
    name: text(p, 'name'),
    toPar: text(p, 'scoreToPar', 'totalToPar', 'total'),
    thru: text(p, 'thru', 'hole', 'today'),
    teeTime: text(p, 'tee_time', 'tee_time_utc'),
  }))
  return { name: text(node, 'name'), rows }
}

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Golf "centre" for a tour (PGA/DP/LIV/LPGA/Champions). Two views:
 *  • Clasificación — the live leaderboard (position, player, score-to-par, thru).
 *  • Salidas — players grouped by tee time (flights): golf is stroke-play so
 *    there are no head-to-head fixtures, and the tee-time flight is the closest
 *    thing to a "match". Winner outright markets render separately via
 *    sports-outright.
 */
export function GolfLeaderboard({
  tour = 'pga',
  lang = 'es',
  limit,
  heading,
  refresh,
  apiBase,
}: GolfLeaderboardProps) {
  const feedPath = TOUR_FEED[tour] ?? 'live'
  const max = number(limit, 20)
  const refreshSeconds = number(refresh, 60)
  const base = resolveApiBase(apiBase)
  const t = useLabels(base, lang)
  const [view, setView] = useState<'board' | 'pairings'>('board')

  const { data, error, loading } = useFeed(
    (signal) => new SportsApiClient(base).feed('golf', feedPath, signal),
    [base, feedPath],
    refreshSeconds,
  )

  const board = data ? parseLeaderboard(data) : { name: '', rows: [] }
  const rows = board.rows.slice(0, max)

  // Group the FULL field by tee time for the pairings view (not capped by limit).
  const flights = (() => {
    const groups = new Map<string, GolfRow[]>()
    for (const r of board.rows) {
      if (!r.teeTime) continue
      if (!groups.has(r.teeTime)) groups.set(r.teeTime, [])
      groups.get(r.teeTime)!.push(r)
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  })()

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{heading ?? 'Golf'}</span>
      </div>

      {loading && <div class="sports-note">{t('loading', 'Cargando…')}</div>}
      {!loading && error && (
        <div class="sports-note error">{t('tableUnavailable', 'La clasificación no está disponible.')}</div>
      )}
      {!loading && !error && board.rows.length === 0 && (
        <div class="sports-note">{t('noStandings', 'Sin datos ahora mismo.')}</div>
      )}

      {board.rows.length > 0 && (
        <>
          <div class="gview">
            <button class={view === 'board' ? 'on' : ''} onClick={() => setView('board')}>
              {t('leaderboard', 'Clasificación')}
            </button>
            <button
              class={view === 'pairings' ? 'on' : ''}
              onClick={() => setView('pairings')}
              disabled={flights.length === 0}
            >
              {t('teeTimes', 'Salidas')}
            </button>
          </div>

          {board.name && <div class="tour">{board.name}</div>}

          {view === 'board' &&
            rows.map((r, i) => {
              const under = r.toPar.trim().startsWith('-')
              return (
                <div class="row" key={r.name}>
                  <span class="pos">{r.pos || i + 1}</span>
                  <span class="name">{r.name}</span>
                  <span class={under ? 'par under' : 'par'}>{r.toPar || 'E'}</span>
                  <span class="thru">{r.thru}</span>
                </div>
              )
            })}

          {view === 'pairings' &&
            (flights.length === 0 ? (
              <div class="sports-note">{t('noTeeTimes', 'Sin salidas programadas.')}</div>
            ) : (
              flights.map(([tee, players]) => (
                <div class="flight" key={tee}>
                  <div class="flight-t">{tee}</div>
                  {players.map((p) => {
                    const under = p.toPar.trim().startsWith('-')
                    return (
                      <div class="flight-p" key={p.name}>
                        <span class="name">{p.name}</span>
                        <span class={under ? 'par under' : 'par'}>{p.toPar || 'E'}</span>
                      </div>
                    )
                  })}
                </div>
              ))
            ))}
        </>
      )}
    </div>
  )
}
